import {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Cartel, Input, Price } from '@artosera/ui';
import { appelDeRequete, dansLaPortee } from '../../api/cles.js';
import { useMutation } from '../../api/mutation.js';
import {
  clesPresentation,
  type ChargePresentation,
  type ElementSelection,
} from '../../api/presentation.js';
import { type SourcePresentation } from '../../api/sourcePresentation.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { usePiegeFocus } from '../usePiegeFocus.js';
import { CalculetteNegociation } from './CalculetteNegociation.js';
import { ErreurPresentation } from './ErreurPresentation.js';
import { RecapitulatifPresentation } from './RecapitulatifPresentation.js';
import {
  cleElementPresentation,
  prixElementPresentation,
  type OeuvrePresentation,
} from './elementsPresentation.js';
import { creerSourcePresentation, interetsEnAttente } from './horsLigne/source.js';
import { useFileHorsLigneFoire } from '../stand/useFileHorsLigneFoire.js';
import styles from './ModePresentation.module.css';

export function ModePresentation({ source }: { source?: SourcePresentation }) {
  const { t } = useTranslation('presentation');
  const { galerieSlug, galerie, compte } = useShellContext();
  const { id = '' } = useParams();
  if (
    !galerieAModule(galerie, 'rendez-vous-client') ||
    !galerieALaction(galerie, 'presentation:read')
  )
    return <p role="alert">{t('interdit')}</p>;
  return <Presentation key={`${compte.id}:${galerieSlug}:${id}`} id={id} source={source} />;
}

function Presentation({ id, source: fournie }: { id: string; source?: SourcePresentation }) {
  const { t } = useTranslation('presentation');
  const { galerieSlug, galerie, compte } = useShellContext();
  const automatique = useMemo(
    () => creerSourcePresentation({ compteId: compte.id, galerieSlug }),
    [compte.id, galerieSlug],
  );
  const source = fournie ?? automatique;
  useEffect(() => () => automatique.liberer(), [automatique]);
  useFileHorsLigneFoire(galerieSlug);
  const enAttente = interetsEnAttente({ compteId: compte.id, galerieSlug }, id).length > 0;
  const naviguer = useNavigate();
  const client = useQueryClient();
  const racine = useRef<HTMLDivElement>(null);
  const inactivite = useRef<ReturnType<typeof setTimeout> | null>(null);
  const geste = useRef<{ id: number; x: number; y: number; le: number } | null>(null);
  const dernierToucher = useRef<{ le: number; cle: string } | null>(null);
  const pleinEcranDemande = useRef(false);
  const [pleinEcran, setPleinEcran] = useState(false);
  const [index, setIndex] = useState(0);
  const [prixDevoiles, setPrixDevoiles] = useState<ReadonlySet<string>>(new Set());
  const [agrandie, setAgrandie] = useState(false);
  const [diaporama, setDiaporama] = useState(false);
  const [duree, setDuree] = useState(5);
  const [comparaison, setComparaison] = useState(false);
  const [comparaisonId, setComparaisonId] = useState('');
  const [fond, setFond] = useState('noir');
  const [interfaceVisible, setInterfaceVisible] = useState(true);
  const [recapitulatif, setRecapitulatif] = useState(false);
  const [calculette, setCalculette] = useState(false);
  const charge = useQuery({
    networkMode: 'always',
    queryKey: [...clesPresentation.charge(galerieSlug, id), 'mode'],
    staleTime: 0,
    gcTime: 0,
    queryFn: (contexte) => source.charger(galerieSlug, id, appelDeRequete(contexte)),
  });
  const elements = charge.data?.elements ?? [];
  const courante = elements[index];
  const comparee = comparaison
    ? (elements.find(
        (element) => cleElementPresentation(element) === comparaisonId && element !== courante,
      ) ?? elements.find((element) => element !== courante))
    : undefined;
  const peutEcrire =
    galerieALaction(galerie, 'presentation:write') && !charge.data?.selection.archivee;
  const interet = useMutation(
    dansLaPortee({
      networkMode: 'always',
      mutationFn: (
        operation: { element: ElementSelection; interesse: boolean; cleOperation?: string },
        appel,
      ) => {
        operation.cleOperation ??= crypto.randomUUID();
        return source.marquerInteret(
          galerieSlug,
          id,
          operation.element.oeuvreId,
          { exemplaireId: operation.element.exemplaireId, interesse: operation.interesse },
          { ...appel, cleOperation: operation.cleOperation },
        );
      },
      onSuccess: ({ interet: marque }) => {
        client.setQueryData<ChargePresentation>(
          [...clesPresentation.charge(galerieSlug, id), 'mode'],
          (precedente) =>
            precedente
              ? {
                  ...precedente,
                  interets: [
                    ...precedente.interets.filter(
                      (element) =>
                        cleElementPresentation(element) !== cleElementPresentation(marque),
                    ),
                    marque,
                  ],
                }
              : undefined,
        );
        void client.invalidateQueries({
          queryKey: clesPresentation.toutes(galerieSlug),
          refetchType: 'none',
        });
      },
    }),
  );
  const quitter = useCallback(() => {
    void naviguer(`/${galerieSlug}/presentation/${id}`);
  }, [naviguer, galerieSlug, id]);
  usePiegeFocus(racine, !calculette && !recapitulatif, quitter);
  const demanderPleinEcran = useCallback(() => {
    if (!document.documentElement.requestFullscreen || document.fullscreenElement) return;
    void document.documentElement.requestFullscreen().catch(() => {
      /* Le cadre fixe reste utilisable sans plein écran natif. */
    });
  }, []);
  useEffect(() => {
    function changement() {
      const actif = document.fullscreenElement === document.documentElement;
      setPleinEcran(actif);
      if (!actif && pleinEcranDemande.current) quitter();
      pleinEcranDemande.current = actif;
    }
    document.addEventListener('fullscreenchange', changement);
    demanderPleinEcran();
    return () => {
      document.removeEventListener('fullscreenchange', changement);
      if (pleinEcranDemande.current && document.exitFullscreen)
        void document.exitFullscreen().catch(() => undefined);
    };
  }, [demanderPleinEcran, quitter]);
  function reveiller() {
    setInterfaceVisible(true);
    if (inactivite.current) clearTimeout(inactivite.current);
    inactivite.current = setTimeout(() => setInterfaceVisible(false), 3000);
  }
  useEffect(() => {
    inactivite.current = setTimeout(() => setInterfaceVisible(false), 3000);
    return () => {
      if (inactivite.current) clearTimeout(inactivite.current);
    };
  }, []);
  useEffect(() => {
    if (!diaporama || !elements.length || recapitulatif || calculette) return;
    const minuterie = setInterval(
      () => setIndex((precedent) => (precedent + 1) % elements.length),
      duree * 1000,
    );
    return () => clearInterval(minuterie);
  }, [diaporama, duree, elements.length, recapitulatif, calculette]);
  function aller(position: number) {
    setIndex(Math.max(0, Math.min(position, elements.length - 1)));
    setAgrandie(false);
  }
  function devoiler(element: OeuvrePresentation) {
    if (diaporama || prixElementPresentation(element) === null) return;
    const cle = cleElementPresentation(element);
    setPrixDevoiles((precedents) => {
      const prochains = new Set(precedents);
      if (prochains.has(cle)) prochains.delete(cle);
      else prochains.add(cle);
      return prochains;
    });
  }
  function marquer() {
    if (!courante || !peutEcrire || interet.isPending) return;
    const interesse = !charge.data?.interets.some(
      (element) =>
        element.interesse && cleElementPresentation(element) === cleElementPresentation(courante),
    );
    interet.mutate({
      element: { oeuvreId: courante.oeuvreId, exemplaireId: courante.exemplaireId },
      interesse,
    });
  }
  function touche(evenement: KeyboardEvent<HTMLDivElement>) {
    reveiller();
    if (
      calculette ||
      recapitulatif ||
      (evenement.target instanceof HTMLElement &&
        evenement.target.closest('input,textarea,select,[role="combobox"]'))
    )
      return;
    if (evenement.altKey || evenement.ctrlKey || evenement.metaKey) return;
    switch (evenement.key.toLowerCase()) {
      case 'arrowleft':
        evenement.preventDefault();
        aller(index - 1);
        break;
      case 'arrowright':
        evenement.preventDefault();
        aller(index + 1);
        break;
      case 'p':
        evenement.preventDefault();
        if (courante) devoiler(courante);
        break;
      case 'i':
        evenement.preventDefault();
        if (!evenement.repeat) marquer();
        break;
      case 'z':
        evenement.preventDefault();
        if (!diaporama) setAgrandie((valeur) => !valeur);
        break;
    }
  }
  function debutGeste(evenement: PointerEvent<HTMLDivElement>) {
    if (evenement.target instanceof HTMLElement && evenement.target.closest('button')) return;
    if (evenement.button !== 0 || evenement.isPrimary === false) return;
    geste.current = {
      id: evenement.pointerId,
      x: evenement.clientX,
      y: evenement.clientY,
      le: Date.now(),
    };
    evenement.currentTarget.setPointerCapture?.(evenement.pointerId);
  }
  function finGeste(evenement: PointerEvent<HTMLDivElement>) {
    const debut = geste.current;
    geste.current = null;
    if (!debut || debut.id !== evenement.pointerId) return;
    const dx = evenement.clientX - debut.x;
    const dy = evenement.clientY - debut.y;
    if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy)) {
      aller(index + (dx < 0 ? 1 : -1));
      dernierToucher.current = null;
    } else if (
      Math.abs(dx) < 12 &&
      Math.abs(dy) < 12 &&
      Date.now() - debut.le < 400 &&
      courante &&
      !diaporama
    ) {
      const cle = cleElementPresentation(courante);
      if (dernierToucher.current?.cle === cle && Date.now() - dernierToucher.current.le < 350) {
        setAgrandie((valeur) => !valeur);
        dernierToucher.current = null;
      } else dernierToucher.current = { le: Date.now(), cle };
    }
  }
  const visible = interfaceVisible || calculette || recapitulatif || interet.isError;
  return createPortal(
    <div
      ref={racine}
      role="dialog"
      aria-modal="true"
      aria-label={t('mode.titre')}
      tabIndex={-1}
      className={styles.mode}
      data-fond={fond}
      onPointerMove={reveiller}
      onPointerDown={reveiller}
      onKeyDown={touche}
      onFocusCapture={reveiller}
    >
      {recapitulatif && charge.data ? (
        <RecapitulatifPresentation
          charge={charge.data}
          source={source}
          onRetour={() => setRecapitulatif(false)}
          onQuitter={quitter}
        />
      ) : (
        <>
          <div className={styles.commandes} data-visible={visible}>
            <Button variant="secondary" onClick={quitter}>
              {t('mode.fermer')}
            </Button>
            {!pleinEcran && typeof document.documentElement.requestFullscreen === 'function' ? (
              <Button variant="secondary" onClick={demanderPleinEcran}>
                {t('mode.pleinEcran')}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => aller(index - 1)} disabled={index === 0}>
              {t('mode.precedente')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => aller(index + 1)}
              disabled={index >= elements.length - 1}
            >
              {t('mode.suivante')}
            </Button>
            <label htmlFor="presentation-fond">{t('mode.fond')}</label>
            <select
              className={styles.selecteur}
              id="presentation-fond"
              value={fond}
              onChange={(evenement) => setFond(evenement.target.value)}
            >
              {['noir', 'blanc', 'gris'].map((valeur) => (
                <option key={valeur} value={valeur}>
                  {t(`mode.${valeur}`)}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              aria-pressed={diaporama}
              disabled={!elements.length}
              onClick={() => {
                setDiaporama((valeur) => !valeur);
                setAgrandie(false);
                setComparaison(false);
                setPrixDevoiles(new Set());
              }}
            >
              {t(diaporama ? 'mode.arreter' : 'mode.diaporama')}
            </Button>
            <label htmlFor="presentation-duree">{t('mode.duree')}</label>
            <Input
              id="presentation-duree"
              className={styles.duree}
              type="number"
              min={1}
              max={300}
              value={duree}
              onChange={(evenement) => {
                const valeur = Number(evenement.target.value);
                if (Number.isInteger(valeur) && valeur >= 1 && valeur <= 300) setDuree(valeur);
              }}
            />
            {!diaporama && elements.length > 1 ? (
              <Button
                variant="secondary"
                aria-pressed={comparaison}
                onClick={() => {
                  setComparaison((valeur) => !valeur);
                  setAgrandie(false);
                }}
              >
                {t('mode.comparer')}
              </Button>
            ) : null}
            {comparee ? (
              <>
                <label htmlFor="presentation-comparaison">{t('mode.seconde')}</label>
                <select
                  className={styles.selecteur}
                  id="presentation-comparaison"
                  value={cleElementPresentation(comparee)}
                  onChange={(evenement) => setComparaisonId(evenement.target.value)}
                >
                  {elements
                    .filter((element) => element !== courante)
                    .map((element) => (
                      <option
                        key={cleElementPresentation(element)}
                        value={cleElementPresentation(element)}
                      >
                        {element.oeuvre.titre}
                        {element.exemplaire ? ` (${element.exemplaire.numero})` : ''}
                      </option>
                    ))}
                </select>
              </>
            ) : null}
            {courante && peutEcrire ? (
              <Button
                variant="secondary"
                aria-pressed={charge.data?.interets.some(
                  (element) =>
                    element.interesse &&
                    cleElementPresentation(element) === cleElementPresentation(courante),
                )}
                loading={interet.isPending}
                onClick={marquer}
              >
                {t('mode.interesse')}
              </Button>
            ) : null}
            {enAttente ? <p role="status">{t('horsLigne.enAttente')}</p> : null}
            {courante && !diaporama && !source.horsLigne ? (
              <Button variant="secondary" onClick={() => setCalculette(true)}>
                {t('calculette.titre')}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              disabled={!charge.data || interet.isPending}
              onClick={() => {
                setDiaporama(false);
                setRecapitulatif(true);
              }}
            >
              {t('mode.terminer')}
            </Button>
            {!pleinEcran ? <p>{t('mode.repli')}</p> : null}
          </div>
          {source.horsLigne ? <p role="status">{t('calculette.reseau')}</p> : null}
          {charge.isPending ? <p role="status">{t('chargement')}</p> : null}
          <ErreurPresentation
            erreur={charge.error ?? interet.error}
            recharger={() => {
              if (interet.isError && interet.variables) interet.mutate(interet.variables);
              else void charge.refetch();
            }}
          />
          {charge.data && !elements.length ? <p>{t('mode.vide')}</p> : null}
          <div
            className={styles.images}
            data-comparaison={Boolean(comparee) && !agrandie}
            data-agrandie={agrandie}
            aria-label={t('mode.oeuvres')}
            onPointerDown={debutGeste}
            onPointerUp={finGeste}
            onPointerCancel={() => {
              geste.current = null;
            }}
          >
            {courante ? (
              <Planche
                element={courante}
                prixVisible={!diaporama && prixDevoiles.has(cleElementPresentation(courante))}
                commandesVisibles={visible}
                diaporama={diaporama}
                agrandie={agrandie}
                onPrix={() => devoiler(courante)}
                onAgrandir={() => setAgrandie((valeur) => !valeur)}
              />
            ) : null}
            {comparee && !agrandie ? (
              <Planche
                element={comparee}
                prixVisible={!diaporama && prixDevoiles.has(cleElementPresentation(comparee))}
                commandesVisibles={visible}
                diaporama={diaporama}
                agrandie={false}
                onPrix={() => devoiler(comparee)}
              />
            ) : null}
          </div>
          <nav className={styles.vignettes} data-visible={visible} aria-label={t('mode.vignettes')}>
            {elements.map((element, position) => (
              <button
                type="button"
                key={cleElementPresentation(element)}
                aria-label={t('mode.afficher', {
                  titre: element.oeuvre.titre,
                  exemplaire: element.exemplaire?.numero ?? '',
                })}
                aria-current={position === index ? 'true' : undefined}
                onClick={() => aller(position)}
              >
                {element.imageUrl ? <img src={element.imageUrl} alt="" /> : null}
                <span>
                  {element.oeuvre.titre}
                  {element.exemplaire ? ` (${element.exemplaire.numero})` : ''}
                </span>
              </button>
            ))}
          </nav>
        </>
      )}
      {calculette && courante ? (
        <CalculetteNegociation
          oeuvreId={courante.oeuvreId}
          exemplaireId={courante.exemplaireId}
          devise={courante.oeuvre.devise}
          contactId={charge.data?.selection.contactId}
          selectionId={id}
          onFermer={() => setCalculette(false)}
        />
      ) : null}
    </div>,
    document.body,
  );
}

function Planche({
  element,
  prixVisible,
  commandesVisibles,
  diaporama,
  agrandie,
  onPrix,
  onAgrandir,
}: {
  element: OeuvrePresentation;
  prixVisible: boolean;
  commandesVisibles: boolean;
  diaporama: boolean;
  agrandie: boolean;
  onPrix: () => void;
  onAgrandir?: () => void;
}) {
  const { t, i18n } = useTranslation('presentation');
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { oeuvre } = element;
  const dimensions = oeuvre.dimensions;
  const prix = prixElementPresentation(element);
  return (
    <figure className={styles.planche}>
      <div className={styles.visuel}>
        {element.imageUrl ? (
          <img src={element.imageUrl} alt={oeuvre.titre} draggable={false} />
        ) : (
          <p>{t('mode.sansImage')}</p>
        )}
      </div>
      {!agrandie ? (
        <Cartel
          className={styles.cartel}
          size="lg"
          artist={element.artiste.nom}
          title={oeuvre.titre}
          year={oeuvre.annee ?? undefined}
          medium={oeuvre.technique?.[langue] || oeuvre.technique?.fr || undefined}
          dimensions={
            dimensions && dimensions.hauteur != null && dimensions.largeur != null
              ? {
                  height: dimensions.hauteur,
                  width: dimensions.largeur,
                  depth: dimensions.profondeur ?? undefined,
                  lang: langue,
                }
              : undefined
          }
        />
      ) : null}
      {prixVisible && prix !== null ? (
        <Price className={styles.prix} cents={prix} lang={langue} currency={oeuvre.devise} />
      ) : null}
      {!diaporama ? (
        <div className={styles.outils} data-visible={commandesVisibles}>
          {prix !== null ? (
            <Button variant="secondary" aria-pressed={prixVisible} onClick={onPrix}>
              {t(prixVisible ? 'mode.masquerPrix' : 'mode.devoilerPrix', { titre: oeuvre.titre })}
            </Button>
          ) : null}
          {onAgrandir ? (
            <Button variant="secondary" aria-pressed={agrandie} onClick={onAgrandir}>
              {t(agrandie ? 'mode.reduire' : 'mode.agrandir')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </figure>
  );
}
